<template>
  <LoadSpinner v-if="loading" />
  <div v-else class="listing-card-grid">
    
    <div v-if="hasCampaignFilters" class="listing-filters-wrapper">
       <ItemListFilterText
         v-if="campaignFilters['enable_place_filter'][0] === 'place-filter'"
         v-model="searchQuery"
         :label="translate('Sijainti')"
         :placeholder="translate('Paikkakunta, kaupunginosa, katu tai postinumero')"
       />
       <ItemListFilterRooms
         v-if="campaignFilters['enable_apartment_size_filter'][0] === 'size-filter'"
         v-model="selectedRoomCounts"
         :label="translate('Huoneita')"
         :apartments="apartments"
       />
    </div>

    <div v-if="hasPagination" class="campaign-items-list__pagination">
      <nav class="navigation pagination" :aria-label="translate('Asuntojen sivutus')">
        <h2 class="screen-reader-text">{{ translate('Asuntojen sivutus') }}</h2>
        <div class="nav-links">
          
          <a v-if="canPrevPage()" class="prev page-numbers" @click="goToPreviousPage()">
            <ChevronIcon
              class="campaign-items-list__prev-page"
            />
          </a>
          
          <template v-for="pageNumber in totalPages">
            <a
              v-if="!isCurrentPage(pageNumber)"
              @click="goToPage(pageNumber)"
              class="page-numbers"
              :key="pageNumber" >
                {{ pageNumber }}
            </a>
            <span
              v-else
              aria-current="page"
              class="page-numbers current">
                {{ pageNumber }}
            </span>
          </template>

          <a v-if="canNextPage()" class="next page-numbers" @click="goToNextPage()">
            <ChevronIcon
              class="campaign-items-list__next-page"
            />
          </a>
          
        </div>
      </nav>
    </div>

    <div v-if="hasItems" class="listing-card-grid__grid .campaign-items-list__grid">
      <div
        v-for="apartment in currentPageData"
        class="listing-card-grid__column campaign-items-list__column"
        :key="apartment.id"
      >
        <ApartmentCard
          :apartment="apartment"
          :get-params="{back: true}"
          class="listing-card-grid__item"
        />
      </div>
    </div>
    
    <div v-else class="campaign-items-list__no-results">
        {{ translate('Ei hakutuloksia valituilla suodattimilla.') }}
    </div>

    <div v-if="hasPagination" class="campaign-items-list__pagination">
      <nav class="navigation pagination" :aria-label="translate('Asuntojen sivutus')">
        <h2 class="screen-reader-text">{{ translate('Asuntojen sivutus') }}</h2>
        <div class="nav-links">
          
          <a v-if="canPrevPage()" class="prev page-numbers" @click="goToPreviousPage()">
            <ChevronIcon
              class="campaign-items-list__prev-page"
            />
          </a>
          
          <template v-for="pageNumber in totalPages">
            <a
              v-if="!isCurrentPage(pageNumber)"
              @click="goToPage(pageNumber)"
              class="page-numbers"
              :key="pageNumber">
                {{ pageNumber }}
            </a>
            <span
              v-else
              aria-current="page"
              class="page-numbers current">
                {{ pageNumber }}
            </span>
          </template>

          <a v-if="canNextPage()" class="next page-numbers" @click="goToNextPage()">
            <ChevronIcon
              class="campaign-items-list__next-page"
            />
          </a>
          
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import globals from '../lib/globals';
import utils from './mixins/utils';
import LoadSpinner from './components/LoadSpinner';
import HousingLocalityCard from './components/HousingLocalityCard';
import ApartmentCard from './components/ApartmentCard';
import ItemListFilterText from './components/ItemListFilterText';
import ItemListFilterRooms from './components/ItemListFilterRooms';
import ChevronIcon from './icons/ChevronIcon';

export default {
  components: {
    HousingLocalityCard, 
    LoadSpinner, 
    ApartmentCard, 
    ItemListFilterText,
    ItemListFilterRooms,
    ChevronIcon
  },
  mixins: [
    utils.translate
  ],
  props: {
    group: { type: String, default: 'old' },
    code: { type: String, default: '' },
    campaignFilters: { type: Object, default: '' },
    itemsPerPage: { type: Number, default: 20 },
    currentPage: { type: Number, default: 1 },
    totalItems: { type: Number, default: 0 }
  },
  data() {
    return {
      loading: false,
      apartments: [],
      searchQuery: "",
      selectedRoomCounts: [],
      localCurrentPage: 1
    };
  },
  watch: {
    // Sync prop if it changes (optional)
    currentPage(val) {
      this.localCurrentPage = val;
    },
    // Reset to page 1 when search query changes
    searchQuery() {
      this.localCurrentPage = 1;
    },
    selectedRoomCounts() {
      this.localCurrentPage = 1;
    }
  },
  computed: {
    /**
     * CORE FILTERING LOGIC
     */
    filteredApartments() {

      let filtered = this.apartments;

      // 1. Apply Text Filter
      if (this.searchQuery) {
        const term = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(apt => {
          const check = (val) => val && val.toString().toLowerCase().includes(term);
          return (
            check(apt.mpc_katuosoite) ||
            check(apt.housing_locality.mpc_name) ||
            check(apt.city.mpc_name) ||
            check(apt.township.mpc_name) ||
            check(apt.building.mpc_postinumero)
          );
        });
      }
      
      // 2. Apply Room Count Filter
      if (this.selectedRoomCounts.length > 0) {
        filtered = filtered.filter(apt => {
          // We assume the API provides a numeric 'room_count' field
          const aptRooms = Number(apt.room_count);

          // Check if the apartment's room count matches *any* of the selected options
          return this.selectedRoomCounts.some(selectedOption => {
            if (selectedOption === 5) {
              // The value 5 represents "5 or more"
              return aptRooms >= 5;
            } else {
              // Exact match for 1, 2, 3, 4
              return aptRooms === selectedOption;
            }
          });
        });
      }

      return filtered;
    },

    totalPages() {
      const list = this.filteredApartments; 
      if (!Array.isArray(list) || list.length === 0) {
        return 0;
      }
      return Math.ceil(list.length / this.itemsPerPage);
    },

    /**
     * Update this to use filteredApartments AND localCurrentPage
     */
    currentPageData() {
      const list = this.filteredApartments;
      
      const startIndex = (this.localCurrentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + Number(this.itemsPerPage);
      
      if (!Array.isArray(list) || list.length === 0) {
        return [];
      }

      return list.slice(startIndex, endIndex);
    },

    hasItems() {
      return this.filteredApartments.length > 0;
    },

    hasPagination() {
      // Check length of filtered items vs items per page
      return this.filteredApartments.length > this.itemsPerPage;
    },
    
    hasCampaignFilters() {
        return Object.values(this.campaignFilters).some(arr => arr.length > 0);
    },
  },
  methods: {
    goToPage(pageNumber) {
      this.localCurrentPage = pageNumber;
    },
    isCurrentPage(pageNumber) {
      return this.localCurrentPage === pageNumber;
    },
    canPrevPage() {
      return this.localCurrentPage > 1;
    },
    canNextPage() {
      return this.localCurrentPage < this.totalPages;
    },
    goToPreviousPage() {
      if(this.canPrevPage()) this.localCurrentPage--;
    },
    goToNextPage() {
      if(this.canNextPage()) this.localCurrentPage++;
    }
  },
  async beforeMount() {
    this.loading = true;
    this.localCurrentPage = this.currentPage; // Initialize local page
    
    try {
      const url = `${globals.urlIntegrationApi}/public/available-apartments-by-campaign-code/${this.code}`;
      const response = await axios.get(url);
      this.apartments = response?.data || [];
    } catch (e) {
      console.error("Failed to fetch apartments", e);
      this.apartments = [];
    }
    this.loading = false;
  }
};
</script>

<style lang="scss" scoped>
@import "~styles/definitions";

.listing-filters-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 1.5rem;
  width: 100%;
}

.item-list-filter-text {
  flex: 1 1 18.75rem; // Allow search bar to grow slightly
  width: auto !important;
  max-width: 28rem;
  min-width: 15.5rem;
}

.item-list-filter-rooms {
  flex: 0 0 auto;
}

@media (max-width: 600px) {
  .listing-filters-wrapper {
    flex-direction: column;
    align-items: center; /* Center items in the column */
  }

  .item-list-filter-text,
  .item-list-filter-rooms {
    width: 100% !important; /* Force full width on mobile */
    max-width: 100%;
    min-width: 0;
  }
}

.campaign-items-list__pagination {
  margin-bottom: 1rem;
  a.page-numbers {
    cursor: pointer;
  }
  .next, .prev {
    position: relative;
  }
  .campaign-items-list__prev-page,
  .campaign-items-list__next-page {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .campaign-items-list__prev-page {
    transform: translate(-50%, -50%) rotate(180deg);
  }
}

.listing-card-grid__grid {
  margin-top: 1.5rem;
}
.campaign-items-list__no-results {
  font-weight: 700;
  max-width: 100%;
  text-align: center;
  margin-top: 2rem;
}

</style>
